import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import EditButton from '../../ui/EditButton';
import { getCurrencyMeta } from '../../../utils/currency';
import { getYearlyBaseMinor } from '../../../utils/subscriptionPricing';
import { formatCadenceAdjustmentLabel } from '../../../utils/cadenceAdjustments';
import MembershipBreakdownModal from '../components/modals/MembershipBreakdownModal';
import { summarizeMemberships } from '../../../utils/membershipReporting';
import { EMPTY_VALUE_LABEL, NOT_APPLICABLE_LABEL } from '../../../utils/formFieldPresentation';

function SubscriptionsCard({ onEdit, canEdit = false, teamId, refreshKey, memberProfiles = [], membershipMode = 'hosted_stripe' }) {
  const [meta, setMeta] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const membershipSummary = summarizeMemberships(memberProfiles);
  const hostedPayments = membershipMode === 'hosted_stripe' || membershipMode === 'hybrid';

  useEffect(() => {
    const load = async () => {
      if (!teamId) return;

      const setupRef = doc(db, 'teams', teamId, 'stripe', 'setup');
      const snap = await getDoc(setupRef);

      if (!snap?.exists?.()) {
        setMeta(null);
        return;
      }

      const d = snap.data() || {};

      const currency = d.currency || 'GBP';
      const { symbol, decimals } = getCurrencyMeta(currency);
      const baseMonthly = Number(d.baseRate ?? 0) / Math.pow(10, decimals);
      const baseYearly =
        getYearlyBaseMinor({
          baseRate: d.baseRate,
          baseRateYearly: d.baseRateYearly,
        }) / Math.pow(10, decimals);

      // Cadence
      const cadenceMode = d.cadenceMode || 'fixed';
      let cadenceSummary = 'Yearly';
      if (cadenceMode === 'fixed') {
        const unit = d?.fixedInterval?.unit === 'month' ? 'month' : 'year';
        cadenceSummary = `${unit === 'month' ? 'Monthly' : 'Yearly'}`;
      } else {
        const monthlyDisc = Number(d?.memberChoice?.monthly?.discountPct || 0);
        const yearlyDisc = Number(d?.memberChoice?.yearly?.discountPct || 0);
        cadenceSummary = `Member chooses (Monthly ${formatCadenceAdjustmentLabel(monthlyDisc, { includeZero: true })},
          Yearly ${formatCadenceAdjustmentLabel(yearlyDisc, { includeZero: true })})`;
      }

      // Tag overrides
      const tagOverrides = d.tagOverrides || {};
      const tagOverrideCount = Object.keys(tagOverrides).length;
      const tagProrationMode =
        d.membershipYearTagProrationMode === 'all' || d.membershipYearTagProrationMode === 'none' || d.membershipYearTagProrationMode === 'selected'
          ? d.membershipYearTagProrationMode
          : d.membershipYearProrateTaggedPrices === false
            ? 'none'
            : 'all';
      const membershipYearProratedTagNames = Array.isArray(d.membershipYearProratedTagNames) ? d.membershipYearProratedTagNames.filter((name) => typeof name === 'string' && name.trim() !== '') : [];
      const membershipYearProrationRatio = d.membershipYearProrationRatio === 'week' || d.membershipYearProrationRatio === 'month' ? d.membershipYearProrationRatio : 'day';
      const membershipYearFullPriceWindowUnit = d.membershipYearFullPriceWindow?.unit === 'day' ? 'day' : 'month';
      const membershipYearFullPriceWindowMaximum = membershipYearFullPriceWindowUnit === 'day' ? 366 : 12;
      const rawMembershipYearFullPriceWindowValue = Number(d.membershipYearFullPriceWindow?.value);
      const membershipYearFullPriceWindowValue =
        Number.isInteger(rawMembershipYearFullPriceWindowValue) && rawMembershipYearFullPriceWindowValue >= 0 && rawMembershipYearFullPriceWindowValue <= membershipYearFullPriceWindowMaximum
          ? rawMembershipYearFullPriceWindowValue
          : 0;
      const familyTiers = Array.isArray(d.familyTiers)
        ? d.familyTiers.map((tier, index) => ({
            id: typeof tier?.id === 'string' && tier.id.trim() ? tier.id.trim() : `family_tier_${index + 1}`,
            label: typeof tier?.label === 'string' && tier.label.trim() ? tier.label.trim() : `Family ${index + 1}`,
            maxParents: Math.max(1, Number(tier?.maxParents || 1)),
            maxChildren: Math.max(0, Number(tier?.maxChildren || 0)),
            baseRate: Number(tier?.baseRate || 0) / Math.pow(10, decimals),
            baseRateYearly: Number(tier?.baseRateYearly || 0) / Math.pow(10, decimals),
          }))
        : [];

      setMeta({
        baseMonthly,
        baseYearly,
        platformFeeHandling: d.platformFeeHandling ?? 'add', // 'add' | 'deduct'
        stripeFeeHandling: d.stripeFeeHandling ?? 'deduct', // 'add' | 'deduct'
        cadenceMode,
        fixedUnit: d?.fixedInterval?.unit === 'month' ? 'month' : 'year',
        cadenceSummary,
        tagOverrideCount,
        membershipYearEnabled: Boolean(d.membershipYearEnabled),
        membershipYearProrateBasePrice: d.membershipYearProrateBasePrice !== false,
        membershipYearTagProrationMode: tagProrationMode,
        membershipYearProratedTagNames,
        membershipYearProrationRatio,
        membershipYearFullPriceWindow: {
          value: membershipYearFullPriceWindowValue,
          unit: membershipYearFullPriceWindowUnit,
        },
        membershipYearStartMonth: Number(d.membershipYearStartMonth || 1),
        membershipYearStartDay: Number(d.membershipYearStartDay || 1),
        nonPaymentRemovalDays:
          Number.isInteger(Number(d.nonPaymentRemovalDays)) && Number(d.nonPaymentRemovalDays) >= 1 && Number(d.nonPaymentRemovalDays) <= 365 ? Number(d.nonPaymentRemovalDays) : 30,
        familyTiers,
        currency,
        currencySymbol: symbol,
        currencyDecimals: decimals,
        currencyLocked: Boolean(d.currencyLocked),
        externalPaymentDisplayMode: d.externalPaymentDisplayMode || 'instructions_only',
        externalPaymentInstructions: typeof d.externalPaymentInstructions === 'string' ? d.externalPaymentInstructions : '',
      });
    };

    load();
  }, [hostedPayments, teamId, refreshKey]);

  const Row = ({ label, value }) => (
    <p className="text-sm text-muted">
      <strong>{label}:</strong> {value ?? EMPTY_VALUE_LABEL}
    </p>
  );

  return (
    <div className="team-card membership-payments-card">
      <div className="card-header-with-icon">
        <span className="card-title">Membership Payments</span>
        <EditButton section="membership payments" label="Manage" onEdit={canEdit ? onEdit : null} />
      </div>

      <div className="card-body membership-payments-card__body">
        {!hostedPayments ? (
          <div className="subscription-meta space-y-1">
            {membershipMode === 'hybrid' && <Row label="Current arrangement" value="Member choice · online or offline" />}
            <Row
              label="Current arrangement"
              value={membershipMode === 'club_gated' ? 'Club-managed · gated' : 'Club-managed · pay later'}
            />
            <p className="text-muted">
              The club collects payments directly. Manage annual headcount billing
              and reconciliation in payment settings.
            </p>
            <Row
              label="Joining information"
              value={!meta
                ? 'Instructions-only fallback'
                : meta.externalPaymentDisplayMode === 'price_and_instructions'
                  ? 'Price and instructions'
                  : meta.externalPaymentDisplayMode === 'price_only'
                    ? 'Price only'
                    : 'Instructions only'}
            />
            <Row
              label="Members"
              value={`${membershipSummary.totalMembers} member${membershipSummary.totalMembers === 1 ? '' : 's'}`}
            />
          </div>
        ) : !meta ? (
          <p className="text-muted">No subscriptions configured.</p>
        ) : (
          <div className="subscription-meta space-y-1">
            <Row
              label={meta.cadenceMode === 'fixed' && meta.fixedUnit === 'year' ? 'Base Rate (per year)' : 'Base Rate (per month)'}
              value={`${meta.currencySymbol}${(meta.cadenceMode === 'fixed' && meta.fixedUnit === 'year' ? meta.baseYearly : meta.baseMonthly).toFixed(meta.currencyDecimals)}`}
            />
            <Row label="Billing Frequency" value={meta.cadenceSummary} />
            {hostedPayments && <Row label="Non-Payment Grace Period" value={`${meta.nonPaymentRemovalDays} day${meta.nonPaymentRemovalDays === 1 ? '' : 's'}`} />}
            {hostedPayments && <Row label="Member Nest Fee" value={meta.platformFeeHandling === 'deduct' ? 'Deduct from club payout' : 'Add to member total'} />}
            {hostedPayments && <Row label="Stripe Fee" value={meta.stripeFeeHandling === 'deduct' ? 'Deduct from club payout' : 'Add to member total'} />}
            {membershipMode === 'hybrid' && <Row label="Offline option" value="Club price and payment instructions · access pending admin approval" />}
            <Row label="Tag-Based Prices" value={meta.tagOverrideCount > 0 ? `${meta.tagOverrideCount} override${meta.tagOverrideCount > 1 ? 's' : ''}` : 'None'} />
            <Row
              label="Membership Year"
              value={
                meta.membershipYearEnabled
                  ? `${new Date(2000, meta.membershipYearStartMonth - 1, meta.membershipYearStartDay).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} (yearly pro-rata)`
                  : 'Off'
              }
            />
            <Row label="Base Joiners" value={meta.membershipYearEnabled ? (meta.membershipYearProrateBasePrice ? 'Prorated' : 'Full yearly base price') : NOT_APPLICABLE_LABEL} />
            <Row
              label="Pro-Rata Ratio"
              value={meta.membershipYearEnabled ? (meta.membershipYearProrationRatio === 'week' ? 'Weekly' : meta.membershipYearProrationRatio === 'month' ? 'Monthly' : 'Daily') : NOT_APPLICABLE_LABEL}
            />
            <Row
              label="Full-Price Window"
              value={
                meta.membershipYearEnabled
                  ? meta.membershipYearFullPriceWindow.value > 0
                    ? `${meta.membershipYearFullPriceWindow.value} ${meta.membershipYearFullPriceWindow.unit}${meta.membershipYearFullPriceWindow.value === 1 ? '' : 's'} after renewal`
                    : 'None (pro-rata applies immediately)'
                  : NOT_APPLICABLE_LABEL
              }
            />
            <Row
              label="Tagged Joiners"
              value={
                meta.membershipYearEnabled
                  ? meta.membershipYearTagProrationMode === 'all'
                    ? 'All priced tags prorated'
                    : meta.membershipYearTagProrationMode === 'none'
                      ? 'No priced tags prorated'
                      : `Selected priced tags (${meta.membershipYearProratedTagNames.length})`
                  : NOT_APPLICABLE_LABEL
              }
            />
            <Row label="Currency" value={`${meta.currency}${meta.currencyLocked ? ' (locked after first payment)' : ''}`} />
            <Row label="Family Tiers" value={meta.familyTiers.length > 0 ? `${meta.familyTiers.length} configured` : 'Off'} />
            <Row
              label="Active Memberships"
              value={`${membershipSummary.totalMembers} member${membershipSummary.totalMembers === 1 ? '' : 's'} · ${membershipSummary.familyMemberships} family membership${membershipSummary.familyMemberships === 1 ? '' : 's'}`}
            />
            {meta.familyTiers.map((tier) => (
              <Row
                key={tier.id}
                label={tier.label}
                value={`${
                  meta.cadenceMode === 'member_choice'
                    ? `${meta.currencySymbol}${tier.baseRate.toFixed(meta.currencyDecimals)}/mo, ${meta.currencySymbol}${tier.baseRateYearly.toFixed(meta.currencyDecimals)}/yr`
                    : meta.fixedUnit === 'month'
                      ? `${meta.currencySymbol}${tier.baseRate.toFixed(meta.currencyDecimals)}/mo`
                      : `${meta.currencySymbol}${tier.baseRateYearly.toFixed(meta.currencyDecimals)}/yr`
                } • Max ${tier.maxParents} parent(s), ${tier.maxChildren} child(ren)`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="card-reporting-footer">
        <button type="button" className="btn btn--secondary btn--xs card-reporting-action" onClick={() => setBreakdownOpen(true)} aria-label="Open membership reporting">
          View reporting <span aria-hidden="true">→</span>
        </button>
      </div>
      <MembershipBreakdownModal isOpen={breakdownOpen} onClose={() => setBreakdownOpen(false)} teamId={teamId} members={memberProfiles} familyTiers={meta?.familyTiers || []} />
    </div>
  );
}

export default SubscriptionsCard;
